import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  NeogovTopNavbarElement,
  NeoTopNavbarNotificationItem,
} from '@neogov/top-navbar-definition';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  filter,
  interval,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { LoggerProvider } from '@shared';
import { AppConfigService } from '../../config/app-config.service';
import { DialogProvider } from '../dialog/dialog.provider';
import { DialogSize } from '../dialog/dialog.types';
import {
  NotificationDetailModalComponent,
  type NotificationDetailModalData,
  type NotificationDetailModalResult,
} from '../../../presentation/modals/notification-detail-modal';
import { INVESTIGATIONS_NOTIFICATIONS_CONFIG } from '../../../presentation/pages/notifications/notifications-investigations.config';

interface NotificationDto {
  id: number;
  notificationType: string;
  title: string;
  body: string;
  isRead: boolean;
  createdOn: string;
  entityId: number | null;
  entityNumber: string | null;
  link: string | null;
}

interface InboxResponseDto {
  data: NotificationDto[];
  total: number;
  unreadCount: number;
}

/**
 * Drives the top-navbar bell (badge + dropdown items) for the Investigations inbox. Investigations
 * analog of NotificationsNavbarService (Cases) — re-implemented, not shared, per the per-context
 * convention.
 *
 * Coordination with the Cases bell service (both share the single navbar bell): this service only
 * *writes* the bell when the user is inside the Investigations area, and deliberately leaves the
 * bell untouched otherwise. The Cases service owns the "disable when outside /cases" write. As long
 * as this service is initialized AFTER the Cases service, it wins inside /investigations while the
 * Cases service wins inside /cases, and neither clobbers the other.
 */
@Injectable({ providedIn: 'root' })
export class InvestigationsNotificationsNavbarService implements OnDestroy {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(DialogProvider);
  private readonly _logger = inject(LoggerProvider).createScoped('InvNotificationsNavbar');
  private readonly _destroy$ = new Subject<void>();

  /** Reused to map raw notification type enums to the human labels shown in the detail modal. */
  private readonly _notificationsConfig = INVESTIGATIONS_NOTIFICATIONS_CONFIG;

  private _navbar: NeogovTopNavbarElement | null = null;
  private static readonly PreviewSize = 10;
  private static readonly PollIntervalMs = 60_000;

  initialize(navbar: NeogovTopNavbarElement): void {
    this._navbar = navbar;
    this._wireInitialState();

    // Refresh on navigations that land in investigations (or stay in investigations).
    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this._destroy$),
      )
      .subscribe(() => this._wireInitialState());

    // Light polling so the badge stays roughly fresh without WebSockets.
    interval(InvestigationsNotificationsNavbarService.PollIntervalMs)
      .pipe(
        startWith(0),
        switchMap(() => (this._isInInvestigationsArea() ? this._fetchInboxPreview() : EMPTY)),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  private _wireInitialState(): void {
    if (!this._navbar) return;

    // Outside the investigations area we leave the bell untouched — the Cases bell service owns the
    // disable/enable in its own area. Writing here would clobber it during /cases navigation.
    if (!this._isInInvestigationsArea()) return;

    this._fetchInboxPreview().subscribe();
  }

  private _fetchInboxPreview(): Observable<void> {
    return this._http
      .get<InboxResponseDto>(
        `${this._config.versionedApiUrl}/investigations/notifications?unreadOnly=false&Take=${InvestigationsNotificationsNavbarService.PreviewSize}&Skip=0`,
      )
      .pipe(
        takeUntil(this._destroy$),
        map((response) => {
          this._applyNotifications(response);
        }),
        catchError((err) => {
          this._logger.warn('Failed to load notification preview', err);
          return EMPTY;
        }),
      );
  }

  private _applyNotifications(response: InboxResponseDto): void {
    if (!this._navbar) return;

    const items: NeoTopNavbarNotificationItem[] = response.data.map((dto) => ({
      id: dto.id.toString(),
      // Concatenate title + body — the navbar component renders a single subject line and has no
      // separate body slot. The em-dash keeps the title scannable while still surfacing context.
      subject: dto.body ? `${dto.title} — ${dto.body}` : dto.title,
      dateTime: dto.createdOn,
      source: 'Case',
      product: 'Case Manager',
      isRead: dto.isRead,
      clickable: {
        type: 'button' as const,
        onClick: () => {
          this._openNotificationDetail(dto);
          return {};
        },
      },
    }));

    this._navbar.notifications.setNotifications({
      isEnabled: true,
      unreadNotificationCount: response.unreadCount,
      notificationItems: items,
      markAllAsRead: {
        clickable: {
          type: 'button' as const,
          onClick: () => {
            this._markAllRead();
            return {};
          },
        },
      },
      viewAll: {
        clickable: {
          type: 'button' as const,
          onClick: () => {
            this._router.navigate(['/investigations/notifications']);
            return {};
          },
        },
      },
    });
  }

  /**
   * Opens the notification detail modal from the bell (mirrors the inbox page's row click) instead
   * of jumping to the Unread tab. Marking read on open keeps the badge and Unread list in sync
   * regardless of which modal action the user picks; "Open case" then routes to the record.
   */
  private _openNotificationDetail(dto: NotificationDto): void {
    if (!dto.isRead) {
      this._markRead(dto.id);
    }

    const dialogRef = this._dialog.open<NotificationDetailModalData, NotificationDetailModalResult>(
      NotificationDetailModalComponent,
      {
        panelClass: 'notification-detail-modal',
        size: DialogSize.md,
        data: {
          id: dto.id,
          title: dto.title,
          body: dto.body,
          typeLabel: this._typeLabel(dto.notificationType),
          entityNumber: dto.entityNumber,
          entityId: dto.entityId,
          createdOn: new Date(dto.createdOn),
          link: dto.link,
          autIdPrefix: this._notificationsConfig.rowAutIdPrefix,
        },
      },
    );

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result !== 'open-entity') return;
        if (dto.entityId) {
          this._router.navigate([this._notificationsConfig.routePrefix, dto.entityId]);
        } else if (dto.link) {
          this._router.navigateByUrl(dto.link);
        }
      });
  }

  /** Raw notification type enum → human label, matching the inbox page's Type column. */
  private _typeLabel(type: string): string {
    return (
      this._notificationsConfig.typeLabels[type] ??
      type
        .replace(this._notificationsConfig.typePrefixPattern, '')
        .replace(/([A-Z])/g, ' $1')
        .trim()
    );
  }

  private _markRead(id: number): void {
    this._http
      .post(`${this._config.versionedApiUrl}/investigations/notifications/${id}/read`, {})
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => this._fetchInboxPreview().subscribe(),
        error: (err) => this._logger.warn('Failed to mark notification as read', err),
      });
  }

  private _markAllRead(): void {
    this._http
      .post(`${this._config.versionedApiUrl}/investigations/notifications/read-all`, {})
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => this._fetchInboxPreview().subscribe(),
        error: (err) => this._logger.warn('Failed to mark all notifications as read', err),
      });
  }

  private _isInInvestigationsArea(): boolean {
    return this._router.url.startsWith('/investigations');
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
